import SubjectSearchService from '@lifeomic/phc-web-toolkit/src/services/SubjectSearchService';
import { useDebounce } from 'use-debounce';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import { useActiveProjectId } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveProjectId';
import { useAsync } from 'react-use';
import { SubjectSuggestion } from '@lifeomic/phc-web-toolkit/src/components/Omnibar/types';
import { useIntl } from 'react-intl';
import { messages } from '@lifeomic/phc-web-toolkit/src/components/Omnibar/messages';
import { getNameFromFHIRRecord } from '@lifeomic/phc-web-toolkit/src/util/fhir';
import { useOmnibarContext } from '@lifeomic/phc-web-toolkit/src/components/Omnibar/OmnibarProvider';
import urls from '../../../util/urls';
import { isFountainLifeAccountId } from '../../../util/fountainLifeAccount';
import { useEffect } from 'react';
import { useMaskedMode } from '@lifeomic/phc-web-toolkit/src/hooks/useMaskedMode';
import {
  useActiveAccountId,
  useFeatures,
} from '@lifeomic/phc-web-toolkit/src/hooks';

export interface Subject {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const subjectSearchService = new SubjectSearchService();

export const useSubjectSuggestions = () => {
  const { formatMessage } = useIntl();
  const activeProjectId = useActiveProjectId();
  const maskedMode = useMaskedMode();
  const hasFeature = useFeatures();
  const activeAccountId = useActiveAccountId();
  const [fhirSubjectSearch] = useService(
    subjectSearchService.fhirSubjectSearch,
    {
      id: 'subject-search',
      name: 'subject-search',
      log: { start: true, success: true, error: true },
      rethrowErrors: true,
    },
  );

  const { searchValue, registerSuggestions, unregisterSuggestions } =
    useOmnibarContext();

  const [debouncedFilter] = useDebounce(searchValue, 500);

  const { value, loading } = useAsync(async () => {
    if (maskedMode || !debouncedFilter || debouncedFilter.trim().length < 3)
      return;
    const res = await fhirSubjectSearch({
      projectId: activeProjectId,
      body: {
        name: debouncedFilter.trim().split(/\s+/),
      },
    });
    if (res.data) {
      return res.data.entry.map(e => e.resource);
    }
    return [];
  }, [debouncedFilter, maskedMode]);

  useEffect(() => {
    const subjects = (value || []) as fhir3.Patient[];
    const subjectSuggestions: SubjectSuggestion[] = subjects?.map(subject => {
      const name = getNameFromFHIRRecord(subject.name);
      return {
        value: `${name.given} ${name.family}`,
        subjectId: subject.id!,
        type: 'subject' as const,
        display: formatMessage(messages.navigateToSubject, {
          subject: `${name.given} ${name.family}`,
        }),
      };
    });
    registerSuggestions({
      id: 'subjectSuggestions',
      loading,
      suggestions: subjectSuggestions,
      handler: (s, setRedirectUrl) => {
        setRedirectUrl(
          hasFeature('fountainLifeUX') ||
            isFountainLifeAccountId(activeAccountId)
            ? urls.app.members(s.subjectId)
            : urls.app.patients(s.subjectId),
        );
      },
    });
    return () => {
      unregisterSuggestions('subjectSuggestions');
    };
  }, [
    registerSuggestions,
    value,
    unregisterSuggestions,
    loading,
    searchValue,
    formatMessage,
    hasFeature,
    activeAccountId,
  ]);
};
